import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Component, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import { UntypedFormGroup, UntypedFormArray, Validators, UntypedFormControl, Form, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  TypesService,
  ProductService,
  FormBuilderService,
  RateService,
  SalesService,
  NotificationService
} from '../../../services';
import {
  IndividualLoanPurposeModel,
  IndividualFeeModel,
  IndividualDepositModel,
  IndividualSplitModel
} from '../../../models/widgets/home-loan-details-model';
import * as _ from 'lodash';
import {Decimal} from 'decimal.js';
import {environment} from '../../../../environments/environment';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {DataHandlerService} from '../../../services/data-handler.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { NgFor, NgIf, NgClass, NgSwitch, NgSwitchCase, CurrencyPipe } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  AddressSearchComponent
} from '../../../../../firstmac-material-lib/projects/fmc-elements/src/lib/address-search/address-search.component';
import {
  MatInputCommifiedDirective
} from '../../../../../firstmac-material-lib/projects/fmc-elements/src/lib/commified/mat-input-commified-directive';
import {VxCoreService} from '../../../services/vx-core.service';
import {firstValueFrom} from 'rxjs';

@Component({
    selector: 'app-home-loan-details',
    templateUrl: 'home-loan-details.component.html',
    standalone: true,
    imports: [MatCardModule, MatCardModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatButtonModule, MatFormFieldModule,
      MatFormFieldModule, MatSelectModule, MatSelectModule, NgFor, MatOptionModule, MatOptionModule, NgIf, MatInputModule, MatInputModule,
      MatTooltipModule, NgClass, NgSwitch, NgSwitchCase, CurrencyPipe, AddressSearchComponent, MatInputCommifiedDirective]
})

export class HomeLoanDetailsComponent extends WizardWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public formGroup: any;
  @Input() public applicationType: string;
  @Input() public faType: string;
  @Input() public securityDetails = [];
  @Input() public personalDetails: UntypedFormArray;
  @Input() public trustDetails: UntypedFormArray;
  @Input() public companyDetails: UntypedFormArray;
  @Input() public isSMSF = false;
  @Input() public isFurtherAdvance = false;
  @Input() public tbaAddress = false;
  @Input() public faParentAppId: any = null;
  @Input() public employmentList: UntypedFormArray;
  @Input() public companyIncomeList: UntypedFormArray;
  @Input() public trustIncomeList: UntypedFormArray;
  @Input() public featureSwitches: any[] = [];

  private readonly ALT_DOC_TYPE = 'alt-doc';

  get altDocProductFilterEnabled(): boolean {
    return !!(this.featureSwitches && this.featureSwitches.find(x => x.id === 'fits-3495-ultra-product')?.ext?.enabled);
  }

  public linkedAppParams = [];
  public curLinkedAppCount = 0;
  public securityAddresses = [];

  public purposesFormArray: UntypedFormArray;
  public feesFormArray: UntypedFormArray;
  public depositsFormArray: UntypedFormArray;
  public splitsFormArray: UntypedFormArray;

  public ancillaryPurposeTypes = [];
  public primaryPurposeTypes = [];
  public feeTypes = [];
  public feeDescriptionTypes = [];
  public depositTypes = [];
  public repaymentTypes = [];
  public loanProducts = [];
  private basProducts: any[] = [];
  public loanTerms = [];
  public loanTermsIO = [];
  public loanTermsFixed = [];
  public productHoneymoons = [];
  public loanSacrifices = [];
  public allLenders = [];
  public allowSacrifice = true; // TODO: Get from product selected?
  public securityTotal = 0;
  public lvr: any;
  public splitWarnings = [];
  public primaryPurpose: string;
  public productWarningSent = false;

  private purposeTypesSlug = 'vx-purposes';  // TODO: update with an application purpose?
  private primaryPurposeTypesSlug = 'vx-application-purpose-types';  // TODO: update with an application purpose?
  private feeTypesSlug = 'fee-types';
  private feeDescriptionTypesSlug = 'fee-type-types';
  private depositTypesSlug = 'deposit-types';
  private repaymentTypesSlug = 'repayment-types';

  public productsInitialized = false;
  private addressesRequired = false;

  public subscriptions = [];
  public allowedPaymentMethods: any = {};
  public defaultPaymentMethods: any = {};
  filteredDisbursementPurposes = [];

  constructor(public dataHandlerService: DataHandlerService,
              private typesService: TypesService,
              private productService: ProductService,
              private formBuilderService: FormBuilderService,
              private rateService: RateService,
              private salesService: SalesService,
              private notificationService: NotificationService,
              private vxCoreService: VxCoreService) {
    super(dataHandlerService);
  }

  get isAltDocRequired(): boolean {
    const hasAltDocIndividual = this.employmentList?.controls.some(person =>
      (person.get('currentEmployments') as UntypedFormArray)?.controls.some(emp =>
        emp.get('status')?.value === 'self-employed' &&
        emp.get('basis')?.value === 'self-employed-bas'
      )
    ) ?? false;

    const hasAltDocCompany = this.companyIncomeList?.controls.some(company =>
      company.get('basIncomeType')?.value === 'business-activity'
    ) ?? false;

    const hasAltDocTrust = this.trustIncomeList?.controls.some(trust =>
      trust.get('basIncomeType')?.value === 'business-activity'
    ) ?? false;

    return hasAltDocIndividual || hasAltDocCompany || hasAltDocTrust;
  }

  private applyAltDocFilter(): void {
    if (this.altDocProductFilterEnabled) {
      if (this.isAltDocRequired) {
        this.loanProducts = _.filter(this.basProducts, (p: any) => p.documentationType.slug === this.ALT_DOC_TYPE);
      } else {
        this.loanProducts = _.filter(this.basProducts, (p: any) => p.documentationType.slug !== this.ALT_DOC_TYPE);
      }
    } else {
      this.loanProducts = [...this.basProducts];
    }
    this.splitsFormArray?.value.forEach((split, idx) => {
      this.updateSelectedProduct(split.productPricing, idx);
    });
  }

  ngOnInit() {
    this.purposesFormArray = this.formGroup.get('purposes') as UntypedFormArray;
    this.feesFormArray = this.formGroup.get('fees') as UntypedFormArray;
    this.depositsFormArray = this.formGroup.get('deposits') as UntypedFormArray;
    this.splitsFormArray = this.formGroup.get('splits') as UntypedFormArray;

    super.ngOnInit();

    this.populateTypes();
    this.initializeForm();

    this.formGroup.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe((val: any) => {
      this.calculate(val);
    });

    this.subscriptions.push(this.productService.productData.subscribe((results) => {
      console.debug('HomeLoanDetailsComponent.productService.productData');
      this.loanProducts = [];
      this.basProducts = [];
      if (results) {
        // console.log('products retrieved', results.output);
        this.productsInitialized = true;
        const sorted = _.sortBy(results.output, 'pricing.pricingSlug');
        if (this.isSMSF === null) {
          this.isSMSF = false;
        }
        this.basProducts = _.filter(sorted, p => p.isSMSF === this.isSMSF);
        this.applyAltDocFilter();
      }
    }));

    if (this.employmentList) {
      this.subscriptions.push(
        this.employmentList.valueChanges.pipe(debounceTime(300)).subscribe(() => {
          this.applyAltDocFilter();
        })
      );
    }

    if (this.companyIncomeList) {
      this.subscriptions.push(
        this.companyIncomeList.valueChanges.pipe(debounceTime(300)).subscribe(() => {
          this.applyAltDocFilter();
        })
      );
    }

    if (this.trustIncomeList) {
      this.subscriptions.push(
        this.trustIncomeList.valueChanges.pipe(debounceTime(300)).subscribe(() => {
          this.applyAltDocFilter();
        })
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngOnChanges(changes) {
    if (changes.tbaAddress) {
      this.updatePurposeAddress();
    }

    if (changes.securityDetails && changes.securityDetails.currentValue) {
      const occupancyType = _.map(_.filter(changes.securityDetails.currentValue, t => t.occupancy), t => t.occupancy).join(',');
      this.securityTotal = _.sumBy(changes.securityDetails.currentValue, s => Number(s.estimatedValue));
      this.securityAddresses = _.map(_.filter(this.securityDetails, t => t.securityAddress), 'securityAddress');
      this.addressesRequired = this.securityAddresses.length > 0;
      this.productService.setProductParameters({occupancyType});
      this.calculateProductLvr();
      this.updatePurposeAddress();
    }
  }

  validateNumberOfSplits(purposeId) {
    const selectedPurpose = _.find(this.primaryPurposeTypes, {id: purposeId});
    if (this.splitsFormArray.controls.length !== 0 && selectedPurpose && selectedPurpose.ext && selectedPurpose.ext.maxSplits) {
      while (this.splitsFormArray.controls.length > Number(selectedPurpose.ext.maxSplits)) {
        this.splitsFormArray.removeAt(this.splitsFormArray.controls.length - 1);
      }
      this.notificationService.toast('warn', `Maximum number of Loan Splits has changed to <b>${selectedPurpose.ext.maxSplits}</b>`);
    }
  }

  updatePurposeAddress() {
    // check that there is a purpose
    if (this.purposesFormArray && this.purposesFormArray.controls.length > 0) {
      let addressPatched = false;
      this.purposesFormArray.controls.forEach((formGroup: any, index: number) => {
        if (formGroup.value.purpose === 'purchase' && formGroup.value.address && !formGroup.value.address.rPDataPropertyID && this.tbaAddress) {
          formGroup.get('address').get('flatSearch').patchValue('TBA');
          formGroup.get('address').get('flatSearch').clearValidators();
          formGroup.get('address').get('flatSearch').updateValueAndValidity();
          formGroup.get('address').get('flatSearch').setErrors(null);
          this.addressesRequired = false;

          console.log('formGroup.get(\'address\')', formGroup.get('address'));
        } else if (!addressPatched && formGroup.value.purpose === 'purchase' && formGroup.value.address && !formGroup.value.address.rPDataPropertyID && !this.tbaAddress) {
          addressPatched = true;
          this.handleDisbursementPurposeChange(index);
        } else if (formGroup.value.purpose !== 'purchase') {
          formGroup.get('address')?.get('flatSearch')?.patchValue('');
        }
      });
    }
  }

  calculateProductLvr() {
    const {
      purposesSubTotal,
      feesFundedTotal,
      depositsTotal,
      loanFacilityTotal
    } = this.formGroup.value;

    const totalValue = Math.max(
      new Decimal(purposesSubTotal).add(feesFundedTotal).sub(depositsTotal).toNumber(),
      Number(loanFacilityTotal)
    );

    if (this.isFurtherAdvance && this.faType !== 'new-security' && this.splitsFormArray && this.splitsFormArray.controls.length > 0 && this.securityDetails && this.securityDetails.length > 0) {
      const existingSecurities: any = this.securityDetails.filter(s => s.securityId);
      const newSecurities: any = this.securityDetails.filter(s => !s.securityId);

      const payload: any = {
        overrides: {
          securities: existingSecurities.map(s => ({
            id: s.securityId,
            estimatedValue: s.estimatedValue
          })),
        },
        additionals: {
          splits: this.splitsFormArray.getRawValue().map(s => ({
            amount: s.splitAmount,
          })),
        }
      };

      if (newSecurities && newSecurities.length > 0) {
        payload.additionals.securities = newSecurities.map(s => ({
          estimated: s.estimatedValue
        }));
      }

      this.vxCoreService.estimatedLvr(this.faParentAppId, payload)
        .subscribe({
          next: (lvrResult: any) => {
            console.log('lvrResult', lvrResult);

            this.lvr = (lvrResult && lvrResult.estimatedLvr) ? lvrResult.estimatedLvr : null;
            this.productService.setProductParameters({lvr: this.lvr});
          },
          error: (error) => {
            console.error('Error fetching estimated LVR:', error);
            this.lvr = 0;
          }
        });
    } else {
      this.lvr = new Decimal(totalValue).div(this.securityTotal).toNumber().toFixed(5);
      this.productService.setProductParameters({lvr: this.lvr});
    }

  }

  populateTypes() {
    this.typesService.getTypesList(this.purposeTypesSlug)
      .then((results) => {
        this.ancillaryPurposeTypes = results.filter(x => x.ext.productCategory.includes('home-loan'));
        this.filteredDisbursementPurposes = [...this.ancillaryPurposeTypes];

        const primaryPurpose = this.formGroup.get('primaryPurpose').value;
        if (primaryPurpose) {
          this.handlePrimaryPurposeChange(primaryPurpose);
        }
      });
    this.typesService.getTypesList(this.primaryPurposeTypesSlug)
      .then((results) => {
        this.primaryPurposeTypes = results.filter(x => x.ext.productCategory === 'home-loan');
      });

    this.typesService.getTypesList(this.feeTypesSlug)
      .then((results) => {
        const channel = environment.auth.active.source;
        const limitFeesTo = _.filter(results, r => {
          return r.ext.limitations && _.find(r.ext.limitations, ['channel', channel]);
        });
        // check if there are should be limited subset of fees against this channel
        if (limitFeesTo && limitFeesTo.length > 0) {
          results = limitFeesTo;
          _.each(limitFeesTo, feeType => {
            const limit = _.find(feeType.ext.limitations, ['channel', channel]);
            if (limit.paymentTypes) {
              this.allowedPaymentMethods[feeType.id] = limit.paymentTypes;
            }
            if (limit.defaultPaymentType) {
              this.defaultPaymentMethods[feeType.id] = limit.defaultPaymentType;
            }
          });
        }
        this.feeTypes = _.filter(results, t => _.get(t, 'ext.productCategoryTypes', []).includes('home-loan'));
        this.checkFeeValidation();
      });

    this.typesService.getTypesList(this.feeDescriptionTypesSlug)
      .then((results) => {
        const exclude = ['payment-refunded'];
        this.feeDescriptionTypes = results.filter(t => !exclude.includes(t.id));
      });
    this.typesService.getTypesList(this.depositTypesSlug)
      .then((results) => {
        this.depositTypes = results;
      });
    this.typesService.getTypesList(this.repaymentTypesSlug)
      .then((results) => {
        this.repaymentTypes = results;
      });
    this.populateLenders();
  }

  initializeForm() {
    this.primaryPurpose = this.formGroup.get('primaryPurpose').value;

    if (this.isSMSF) {
      this.setPurposesForInvSmsf(this.formGroup.get('primaryPurpose').value);
    }

    const initializeValidation = (fa, model) => {
      fa.controls.forEach(fg => {
        this.formBuilderService.setValidationRules(model, fg);
      });
    };

    if (this.purposesFormArray.controls.length === 0) {
      this.addOne('purpose');
    } else {
      initializeValidation(this.purposesFormArray, new IndividualLoanPurposeModel());
    }
    if (this.feesFormArray.controls.length === 0) {
      // this.addOne('fee');
    } else {
      initializeValidation(this.feesFormArray, new IndividualFeeModel());
      this.checkFeeValidation();
    }
    if (this.depositsFormArray.controls.length === 0) {
      // this.addOne('deposit');
    } else {
      initializeValidation(this.depositsFormArray, new IndividualDepositModel());
    }
    if (this.splitsFormArray.controls.length === 0) {
      this.addOne('split');
    } else {
      initializeValidation(this.splitsFormArray, new IndividualSplitModel());
    }

    // on primary purpose change
    this.formGroup.get('primaryPurpose').valueChanges.pipe(debounceTime(500)).subscribe((val) => {
      this.productService.setProductParameters({primaryPurpose: val});
      const match = _.find(this.primaryPurposeTypes, type => type.id === val);
      const purposeGroup = _.get(match, 'ext.loanPurposeGroup');
      const productPurposeGroup = _.get(match, 'ext.productPurposeGroups', []).join(',');
      this.productService.setProductParameters({purposeGroupType: productPurposeGroup || purposeGroup});
    });
  }

  setPurposesForInvSmsf(value) {
    if (this.isSMSF && value === 'buying-an-investment-property') {
      if (this.purposesFormArray.controls.length === 1 && value !== this.primaryPurpose) {
        if (!this.purposesFormArray.at(0).get('purpose').value || this.purposesFormArray.at(0).get('purpose').value === 'purchase') {
          this.purposesFormArray.at(0).get('purpose').patchValue('purchase');

          this.addOne('purpose');

          if (this.purposesFormArray.at(1) && !this.purposesFormArray.at(1).get('purpose').value) {
            this.purposesFormArray.at(1).get('purpose').patchValue('fees-and-costs');
          }
        } else if (this.purposesFormArray.at(0).get('purpose').value !== 'purchase') {
          this.purposesFormArray.at(0).get('purpose').patchValue('purchase');

          if (this.purposesFormArray.at(1)) {
            this.purposesFormArray.at(1).get('purpose').patchValue('fees-and-costs');
          } else {
            this.addOne('purpose');
            this.purposesFormArray.at(1).get('purpose').patchValue('fees-and-costs');
          }
        }
      }
    }

    this.primaryPurpose = value;
  }

  checkFeeValidation() {
    if (this.feeTypes && this.feeTypes.length > 0) {
      this.feesFormArray.controls.forEach((fg: any) => {
        const foundFeeType = this.feeTypes.find(x => x.id === fg.value.feeType);

        console.log('fg', fg);

        if (foundFeeType && foundFeeType.ext.maxHomeLoanFeeValidationAmount) {
          if (fg.get('maxValidationAmount')) {
            fg.get('maxValidationAmount').patchValue(foundFeeType.ext.maxHomeLoanFeeValidationAmount);
          }
          fg.get('feeAmount').clearValidators();
          fg.get('feeAmount').setValidators(Validators.compose([
            Validators.max(foundFeeType.ext.maxHomeLoanFeeValidationAmount),
            Validators.min(1),
            Validators.required,
            Validators.pattern('[0-9.]*')
          ]));
          fg.get('feeAmount').patchValue(fg.value.feeAmount);
        } else {
          if (fg.get('maxValidationAmount')) {
            fg.get('maxValidationAmount').patchValue(null);
          }
          fg.get('feeAmount').clearValidators();
          fg.get('feeAmount').setValidators(Validators.compose([
            Validators.required,
            Validators.min(1),
            Validators.pattern('[0-9.]*')
          ]));
          fg.get('feeAmount').patchValue(fg.value.feeAmount);
        }

        console.log('checkFeeValidation fg', fg);

        fg.updateValueAndValidity();
      });
    }
  }

  addOne(name) {
    let arrayControl: any;
    let fg: any;
    switch (name) {
      case 'purpose':
        arrayControl = <UntypedFormArray>this.purposesFormArray;
        fg = this.formBuilderService.convertObjectToFormGroup(new IndividualLoanPurposeModel());
        this.formBuilderService.setValidationRules(new IndividualLoanPurposeModel(), fg);
        // this.watchPurposeGroup(fg);
        break;

      case 'fee':
        arrayControl = <UntypedFormArray>this.feesFormArray;
        fg = this.formBuilderService.convertObjectToFormGroup(new IndividualFeeModel());
        this.formBuilderService.setValidationRules(new IndividualFeeModel(), fg);
        break;

      case 'deposit':
        arrayControl = <UntypedFormArray>this.depositsFormArray;
        fg = this.formBuilderService.convertObjectToFormGroup(new IndividualDepositModel());
        this.formBuilderService.setValidationRules(new IndividualDepositModel(), fg);
        break;

      case 'split':
        arrayControl = <UntypedFormArray>this.splitsFormArray;
        fg = this.formBuilderService.convertObjectToFormGroup(new IndividualSplitModel());
        this.formBuilderService.setValidationRules(new IndividualSplitModel(), fg);
        break;
    }
    arrayControl.push(fg);
    return fg;
  }

  removeOne(name, index) {
    let arrayControl: any;

    switch (name) {
      case 'purpose':
        arrayControl = <UntypedFormArray>this.purposesFormArray;
        break;

      case 'fee':
        arrayControl = <UntypedFormArray>this.feesFormArray;
        break;

      case 'deposit':
        arrayControl = <UntypedFormArray>this.depositsFormArray;
        break;

      case 'split':

        // shift all the terms
        _.remove(this.loanTerms, (t, i) => i === index);
        _.remove(this.loanTermsIO, (t, i) => i === index);
        _.remove(this.loanTermsFixed, (t, i) => i === index);
        _.remove(this.productHoneymoons, (t, i) => i === index);

        arrayControl = <UntypedFormArray>this.splitsFormArray;
        break;
    }
    console.log('[removeOne]', index, name);
    arrayControl.removeAt(index);
    this.initializeForm();
  }

  async retrieveLinkedAppCount() {
    const allParams = [];
    const personMaps = (this.personalDetails as any).getRawValue().map((p) => {
      const param = {
        type: 'person',
        firstName: p.firstName,
        lastName: p.lastName,
        email: p.email,
        phoneNumber: _.get(p, 'phones.0.phoneNumber'),
        dateOfBirth: p.dateOfBirth,
        driversLicenseNumber: p.driversLicence
      };
      allParams.push(param);
    });

    // if parameters has changed, resubmitt totals`
    // console.log(JSON.stringify(allParams));
    // console.log(JSON.stringify(this.linkedAppParams));
    if (JSON.stringify(allParams) !== JSON.stringify(this.linkedAppParams)) {
      const totals = await Promise.all(allParams.map(p => this.salesService.retrieveLinkedAppCountByPartyDetails(p).toPromise().catch(e => 0)));
      this.curLinkedAppCount = _.sum(totals);
      this.linkedAppParams = allParams;
      return _.sum(totals);
    }
    return this.curLinkedAppCount;
  }

  async runRateAdjustments(splitFg) {
    const {
      referenceRate,
      productPricing: pricingSlug
    } = splitFg.value;
    const linkedAppCount = await this.retrieveLinkedAppCount(); // TODO fix this when search API allows searching based on partyRole / security
    return this.rateService.getDialDownRatesBasic('home-loan', {
      referenceRate,
      pricingSlug,
      linkedAppCount
    }).toPromise().then((val) => {
      // console.log('Dial Down calculated', val);
      const adjustment = new Decimal(_.get(val, 'Adjustment', 0)).mul(100).toNumber(); // multiply it to 100
      splitFg.get('adjustment').patchValue(adjustment);
      splitFg.get('adjustmentType').patchValue(val.AdjustmentType || null);
      // TODO might need to handle expiry dates but not sure how this is going to work
      splitFg.get('adjustmentExpiry').patchValue(val.ExpiryDays || null);
      return adjustment;
    });
  }

  processSplitWarnings(index) {
    const warnings = [];
    if (!this.splitWarnings[index]) { // initialize split warning
      this.splitWarnings[index] = [];
    }

    const processLoanAmountWarnings = () => {
      const slug = this.splitsFormArray.at(index).value.productPricing;
      const product = this.loanProducts.find(x => x.pricing.pricingSlug === slug);
      const loanAmount = Number(this.formGroup.value.loanFacilityTotal || 0);
      const splitAmount = Number(this.splitsFormArray.at(index).value.splitAmount || 0);

      if (product && splitAmount) {
        this.splitsFormArray.at(index).get('splitAmount').setValidators(Validators.min(product.minSplit));
        this.splitsFormArray.at(index).get('splitAmount').markAsTouched();
        this.splitsFormArray.at(index).get('splitAmount').updateValueAndValidity();
      }

      if (product && loanAmount) {
        this.formGroup.get('loanFacilityTotal').setValidators(Validators.min(product.minLoan));
        this.formGroup.get('loanFacilityTotal').markAsTouched();
        this.formGroup.get('loanFacilityTotal').updateValueAndValidity();

        this.formGroup.get('purposesSubTotal').setValidators(Validators.min(product.minLoan));
        this.formGroup.get('purposesSubTotal').markAsTouched();
        this.formGroup.get('purposesSubTotal').updateValueAndValidity();
      }

      if (product) {
        if (product.minLoan && loanAmount < product.minLoan) {
          warnings.push({
            type: 'minLoan',
            data: {
              loanAmount,
              minLoan: product.minLoan
            }
          });
        }

        if (product.maxLoan && loanAmount > product.maxLoan) {
          warnings.push({
            type: 'maxLoan',
            data: {
              loanAmount,
              maxLoan: product.maxLoan
            }
          });
        }

        if (product.minSplit && splitAmount < product.minSplit) {
          warnings.push({
            type: 'minSplit',
            data: {
              splitAmount,
              minSplit: product.minSplit
            }
          });
        }
      }
    };

    processLoanAmountWarnings();
    this.splitWarnings[index] = warnings;
  }

  async updateSelectedProduct(slug, index) {
    const product = this.loanProducts.find(x => x.pricing.pricingSlug === slug);

    this.splitWarnings[index] = [];

    if (!product && this.productsInitialized) {
      this.splitsFormArray.at(index).get('productPricing').setErrors({invalid: true});
      if (slug && !this.productWarningSent) {
        this.productWarningSent = true;
        this.notificationService.toast('warn', 'Product list was updated due to change on filters, kindly select a new product', '');
      }
      return;
    } else {
      this.productWarningSent = false;
    }

    this.processSplitWarnings(index);

    this.splitsFormArray.at(index).get('splitAmount').setValidators([Validators.required, Validators.min(product.minSplit)]);
    this.splitsFormArray.at(index).get('splitAmount').updateValueAndValidity();

    if (index === 0) {
      this.formGroup.get('loanFacilityTotal').setValidators([Validators.required, Validators.min(product.minSplit)]);
    }

    this.splitsFormArray.at(index).get('interestType').patchValue(product.interestType[0]);
    this.splitsFormArray.at(index).get('productPricing').setErrors(null);
    this.splitsFormArray.at(index).get('product').patchValue(product.slug, {emitEvent: false});
    this.splitsFormArray.at(index).get('isShariaCompliant').patchValue(product.isShariaCompliant || null, {emitEvent: false});
    this.splitsFormArray.at(index).get('displayName').patchValue(product.displayName || null, {emitEvent: false});
    if (this.splitsFormArray.at(index).get('allowCards')) {
      this.splitsFormArray.at(index).get('allowCards').patchValue(product.allowCards || null, {emitEvent: false});
    }

    if (this.splitsFormArray.at(index).get('isGreen')) {
      this.splitsFormArray.at(index).get('isGreen').patchValue(_.get(product, 'pricing.isGreen', null), {emitEvent: false});
    }

    if (this.splitsFormArray.at(index).get('isSMSF')) {
      this.splitsFormArray.at(index).get('isSMSF').patchValue(product.isSMSF || null, {emitEvent: false});
    }

    if (this.splitsFormArray.at(index).get('isReferenceRateDisclosed')) {
      this.splitsFormArray.at(index).get('isReferenceRateDisclosed').patchValue(product.isReferenceRateDisclosed || null, {emitEvent: false});
    }

    if (this.splitsFormArray.at(index).get('referenceRate')) {
      this.splitsFormArray.at(index).get('referenceRate').patchValue(_.get(product, 'pricing.reference', null), {emitEvent: false});
    }

    const adjustment = await this.runRateAdjustments(this.splitsFormArray.at(index));

    // populate terms
    if (product) {
      this.loanTerms[index] = [];
      this.loanTermsIO[index] = [];
      this.loanTermsFixed[index] = [];
      this.productHoneymoons[index] = [];

      const parseTerms = (resultArr, termArray) => {
        if (termArray && termArray.length > 0) {
          termArray.forEach((rate) => {
            const term = {
              slug: rate.value + '-' + rate.unit,
              name: rate.value + ' ' + rate.unit,
              rate: rate
            };

            // name should be singular if value is 1
            if (Number(rate.value) === 1) {
              term.name = _.trimEnd(term.name, 's');
            }
            resultArr.push(term);
          });
          this.splitsFormArray.at(index).get('term').setValidators(Validators.required);
        } else {
          this.splitsFormArray.at(index).get('term').patchValue(null);
          this.splitsFormArray.at(index).get('term').setValidators(null);
        }
      };

      parseTerms(this.loanTerms[index], product.terms);
      parseTerms(this.loanTermsIO[index], product.interestOnlyTerms);
      parseTerms(this.loanTermsFixed[index], product.fixedTerms);
      parseTerms(this.productHoneymoons[index], product.honeymoons);

      // will need to pass one of these to home-loan discounts workbook
      this.splitsFormArray.at(index).get('rate').patchValue(product.pricing.rate, {emitEvent: false}); // this will be the split.mdr
      this.splitsFormArray.at(index).get('margin').patchValue(product.pricing.managersMargin || 0, {emitEvent: false}); // this will be split.margin

      const adjustedRtb = new Decimal(product.pricing.rtb || product.pricing.rate || 0).add(adjustment).toNumber();
      this.splitsFormArray.at(index).get('rtb').patchValue(adjustedRtb, {emitEvent: false}); // this will not be sent (only for UI purposes)

      const repaymentType = (product.interestOnlyTerms) ? 'interest-only' : 'pi';
      this.splitsFormArray.at(index).get('repayment').patchValue(repaymentType, {emitEvent: false});

      this.setSplitTerm(this.splitsFormArray.at(index).value.repayment || 'pi', index);
      if (this.splitsFormArray.at(index).value.repayment === 'interest-only' && this.loanTermsIO[index].length === 0) {
        this.splitsFormArray.at(index).get('repayment').patchValue('pi', {emitEvent: false});
      }
      this.syncLoanTerms(index); // sync loan terms based on first split
    } else {
      this.splitsFormArray.at(index).get('product').patchValue(null, {emitEvent: false});
      this.splitsFormArray.at(index).get('product').updateValueAndValidity({emitEvent: false});
      this.splitsFormArray.at(index).get('repayment').patchValue('pi', {emitEvent: false});
    }
  }

  syncLoanTerms(indexThatChanged: number, selectedTerm: string = null) {
    const primaryTerm = this.splitsFormArray.at(0).value.term;
    if (indexThatChanged === 0) {
      this.splitsFormArray.controls.forEach((ctrl, i) => {
        if (i > 0) {
          ctrl.get('term').patchValue(selectedTerm || primaryTerm);
        }
      });
    } else {
      this.splitsFormArray.at(indexThatChanged).get('term').patchValue(selectedTerm || primaryTerm);
    }
  }

  setSplitTerm(slug, index) {
    console.log('[setSplitTerm]', index);
    // first find if the currently selected loan term is applicable
    let loanTerm = this.loanTerms[index].find(t => t.slug === this.splitsFormArray.at(index).value.term) || _.last(this.loanTerms[index]) || {} as any;
    this.splitsFormArray.at(index).get('term').patchValue(loanTerm.slug || null, {emitEvent: false});

    if (slug === 'interest-only') {
      loanTerm = _.find(this.loanTermsIO[index], ['rate.default', true]) || _.first(this.loanTermsIO[index]) || {} as any;
      this.splitsFormArray.at(index).get('interestOnlyTerm').patchValue(loanTerm.slug, {emitEvent: false});
    } else {
      this.splitsFormArray.at(index).get('interestOnlyTerm').patchValue(null, {emitEvent: false});
    }

    if (this.loanTermsFixed[index].length) {
      loanTerm = _.find(this.loanTermsFixed[index], ['rate.default', true]) || _.first(this.loanTermsFixed[index]) || {} as any;
      this.splitsFormArray.at(index).get('fixedRateTerm').patchValue(loanTerm.slug, {emitEvent: false});
    } else {
      this.splitsFormArray.at(index).get('fixedRateTerm').patchValue(null, {emitEvent: false});
    }

    if (this.productHoneymoons[index].length) {
      loanTerm = _.find(this.productHoneymoons[index], ['rate.default', true]) || _.first(this.productHoneymoons[index]) || {} as any;
      this.splitsFormArray.at(index).get('honeymoon').patchValue(loanTerm.slug, {emitEvent: false});
    } else {
      this.splitsFormArray.at(index).get('honeymoon').patchValue(null, {emitEvent: false});
    }
  }

  calculate(data) {
    console.log('[calculate]', data);
    let totalPurposes = 0;
    let totalFundedFee = 0;
    let totalOthersFee = 0;
    let totalDeposits = 0;
    let totalSplits = 0;

    // purposes
    data.purposes.forEach((p) => {
      totalPurposes += +(new Decimal(p.amount || 0).toFixed(2));
    });
    this.formGroup.get('purposesSubTotal').patchValue(totalPurposes.toFixed(2), {emitEvent: false});
    // this.productService.setProductParameters({loanAmount: totalPurposes.toFixed(2)});

    // fees
    data.fees.forEach((f) => {
      if (f.feeDescription === 'funded') {
        totalFundedFee += +(new Decimal(f.feeAmount || 0).toFixed(2));
      } else {
        totalOthersFee += +(new Decimal(f.feeAmount || 0).toFixed(2));
      }
    });
    this.formGroup.get('feesFundedTotal').patchValue(totalFundedFee.toFixed(2), {emitEvent: false});
    this.formGroup.get('feesOtherTotal').patchValue(totalOthersFee.toFixed(2), {emitEvent: false});
    // deposits
    data.deposits.forEach((d) => {
      totalDeposits += +(new Decimal(d.depositAmount || 0).toFixed(2));
    });
    this.formGroup.get('depositsTotal').patchValue(totalDeposits.toFixed(2), {emitEvent: false});
    // splits
    data.splits.forEach((s) => {
      totalSplits += +(new Decimal(s.splitAmount || 0).toFixed(2));
    });
    this.formGroup.get('loanFacilityTotal').patchValue(totalSplits.toFixed(2), {emitEvent: false});

    // check if all values have been allocated
    const unallocatedAmount = +(new Decimal(totalSplits - (totalPurposes + totalFundedFee - totalDeposits)).mul(-1).toFixed(2));
    this.formGroup.get('unallocatedAmount').patchValue(unallocatedAmount, {emitEvent: false});

    this.calculateProductLvr();

    data.splits.forEach((s, index) => {
      this.processSplitWarnings(index);
    });

  }

  handleFeeTypeChange(formGroup: any, feeType) {
    if (this.defaultPaymentMethods[feeType]) {
      formGroup.get('feeDescription').patchValue(this.defaultPaymentMethods[feeType]);
    }
  }

  handleDisbursementPurposeChange(indexThatChanged: number, selectedPurpose: string = null) {
    console.log('handleDisbursementPurposeChange', indexThatChanged);
    if (this.purposesFormArray) {
      const purpose = this.purposesFormArray.at(indexThatChanged) as UntypedFormGroup;
      const purposeDescriptionType = this.checkDescriptionType(purpose);

      if (purposeDescriptionType === 'address') {
        // get the address from security
        if (this.securityAddresses.length > 0) {
          // use the first one
          const address = _.first(this.securityAddresses);
          if (!this.purposesFormArray.at(indexThatChanged).get('address')) {
            purpose.setControl('address', new UntypedFormControl(address));
          }
          this.purposesFormArray.at(indexThatChanged).get('address').patchValue(address);
        }

        if (this.tbaAddress) {
          console.log('updatePurposeAddress formGroup', this.purposesFormArray.at(indexThatChanged));
          setTimeout(() => this.updatePurposeAddress(), 1000);
        }
      } else {
        // remove the address formgroup to disable validations
        if (this.purposesFormArray.at(indexThatChanged).get('address')) {
          purpose.removeControl('address');
          purpose.updateValueAndValidity();
        }
      }
    }
    // console.log('purposeDescriptionType', purposeDescriptionType);
  }

  handlePrimaryPurposeChange(purposeId) {
    const selectedPurpose = _.find(this.primaryPurposeTypes, {id: purposeId});
    if (selectedPurpose && selectedPurpose.ext && selectedPurpose.ext.disbursementPurposes) {
      this.filteredDisbursementPurposes = [];
      selectedPurpose.ext.disbursementPurposes.forEach(id => {
        const foundDisbursementPurpose = _.find(this.ancillaryPurposeTypes, ['id', id]);
        this.filteredDisbursementPurposes.push(foundDisbursementPurpose);
      });
    }

    this.validateDisbursementPurposes();
  }

  validateDisbursementPurposes() {
    this.purposesFormArray.controls.forEach((element: any) => {
      const purposeControl = _.get(element, 'controls.purpose');
      if (!(_.find(this.filteredDisbursementPurposes, {id: purposeControl.value}))) {
        purposeControl.reset();
      }
    });
  }

  public populateLenders() {
    // console.log('[populateLenders]');
    return this.typesService.getTypesList('credit-providers', 'ext.CompanyName')
      .then((results) => {
        // console.log('[populateLenders]', results.length);
        this.allLenders = results.sort((a, b) => {
          return a.text - b.text;
        })
          .map((t) => {
            return {
              name: t.text,
              partyRoleId: t.id,
              partyId: t.ext.PartyID
            };
          });
      });
  }

  checkDescriptionType(formGroup) {
    switch (formGroup.value.purpose) {
      case 'buying-my-home':
      case 'building-a-house':
      case 'buying-an-investment-property':
      case 'refinancing-my-home':
      case 'refinancing-my-investment-property':
      case 'purchase':
      case 'construction':
      case 'home-improvements':
        return 'address';
      case 'external-refinance':
      case 'internal-refinance':
      case 'payout-credit-card':
        return 'credit-provider';
      default:
        return 'description';
    }
  }

  checkDetailType(formGroup) {
    switch (formGroup.value.purpose) {
      case 'internal-refinance':
      case 'external-refinance':
      case 'payout-credit-card':
        return 'number';
      default:
        return 'text';
    }
  }

  flagPurposeTooltip(formGroup) {
    if (formGroup.get('purpose').value === 'external-refinance'
      || formGroup.get('purpose').value === 'internal-refinance'
      || formGroup.get('purpose').value === 'payout-credit-card') {
      return false;
    } else {
      return true;
    }
  }
}
